import { useState, useEffect } from 'react';
import { getAvatarColor } from '../../utils/avatar';
import { getCachedPhotoDataUrl, fetchAndCachePhotoDataUrl } from '../../lib/photo-blob-cache';

interface AvatarProps {
  name: string;
  url?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-16 h-16 text-xl',
  xl: 'w-24 h-24 text-3xl',
};

export const Avatar = ({
  name,
  url,
  size = 'md',
  className = '',
}: AvatarProps) => {
  const [imageError, setImageError] = useState(false);

  // Synchronously seed from localStorage on mount — no flash, no network request
  // when the blob is already cached from a previous session.
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(() =>
    url ? (getCachedPhotoDataUrl(url) ?? url) : null
  );

  const initial = name ? name.charAt(0).toUpperCase() : '?';
  const colorClass = name ? getAvatarColor(name) : 'bg-gray-400';

  // If the blob is not yet in localStorage, kick off a background fetch.
  // fetchAndCachePhotoDataUrl deduplicates concurrent callers via a shared
  // in-flight Promise, so all three Avatar instances on the page share ONE
  // HTTP request instead of each firing their own.
  useEffect(() => {
    if (!url || imageError) return;

    // Already a local data URL — nothing to do.
    const localUrl = getCachedPhotoDataUrl(url);
    if (localUrl) {
      if (localUrl !== resolvedUrl) setResolvedUrl(localUrl);
      return;
    }

    // resolvedUrl is currently the raw remote URL (set by useState init above).
    // Fetch once (shared with other Avatar instances) and swap to data URL.
    let cancelled = false;
    fetchAndCachePhotoDataUrl(url).then((dataUrl) => {
      if (!cancelled && dataUrl) setResolvedUrl(dataUrl);
    });

    return () => { cancelled = true; };
  // resolvedUrl intentionally omitted — we only want to re-run on url/error changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, imageError]);

  if (resolvedUrl && !imageError) {
    return (
      <img
        src={resolvedUrl}
        alt={name}
        onError={() => setImageError(true)}
        className={`${sizeClasses[size]} rounded-full object-cover border border-gray-200 ${className}`}
      />
    );
  }

  // Fallback to initial if no URL or image failed to load
  return (
    <div
      className={`${sizeClasses[size]} rounded-full ${colorClass} text-white flex items-center justify-center font-bold border border-white/20 shadow-sm ${className}`}
    >
      {initial}
    </div>
  );
};
